import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Zap, Diamond, MapPin, Brain, Target, Flame, ClipboardList, Award, Ticket, Coffee,
  GraduationCap, Trophy, TrendingUp, Lightbulb, Star, Lock, Unlock, Check, CheckCircle2,
  User, Mail, Phone, MessageCircle, ShieldCheck,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import icone from "@/assets/icone.png";

const LINK_4X = "https://mpago.li/2Roxf7R";
const LINK_PIX = "https://mpago.la/16gfphN";
const WPP_INTERESSE =
  "https://wa.me/5516981960018?text=Ol%C3%A1%20Junior%21%20Tenho%20interesse%20na%20Imers%C3%A3o%20Seja%20L%C3%ADder%20do%20Seu%20Destino%20%2823%2F07%29.";
const WPP_INFO =
  "https://wa.me/5516981960018?text=Ol%C3%A1%20Junior%21%20Quero%20mais%20informa%C3%A7%C3%B5es%20sobre%20a%20Imers%C3%A3o.";

type FieldState = "" | "ok" | "err";

const maskPhone = (raw: string) => {
  const v = raw.replace(/\D/g, "").substring(0, 11);
  let r = "";
  if (v.length > 0) r = "(" + v.substring(0, 2);
  if (v.length >= 3) r += ") " + v.substring(2, 7);
  if (v.length >= 8) r += "-" + v.substring(7, 11);
  return r;
};
const okEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const okPhone = (p: string) => p.replace(/\D/g, "").length >= 10;

export default function ImersaoLider() {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [tel, setTel] = useState("");
  const [stN, setStN] = useState<FieldState>("");
  const [stE, setStE] = useState<FieldState>("err");
  const [stT, setStT] = useState<FieldState>("");
  const [msgN, setMsgN] = useState("");
  const [msgE, setMsgE] = useState("⚠ E-mail inválido");
  const [msgT, setMsgT] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorTop, setErrorTop] = useState("");

  useEffect(() => {
    const id = "imersao-lider-css";
    const href = "/imersao-lider.css?v=11";
    const existing = document.getElementById(id) as HTMLLinkElement | null;
    if (!existing) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    } else if (existing.href.indexOf("v=8") === -1) {
      existing.href = href;
    }
    const fontId = "imersao-lider-fonts";
    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Cormorant+Garamond:ital,wght@1,500&family=Montserrat:wght@400;500;600;700;800;900&display=swap";
      document.head.appendChild(link);
    }
    const prevBg = document.body.style.background;
    document.body.style.background = "#05080F";
    document.body.classList.add("imersao-lider-page");
    return () => {
      document.body.style.background = prevBg;
      document.body.classList.remove("imersao-lider-page");
    };
  }, []);

  const validateAll = () => {
    let ok = true;
    if (nome.trim().length < 3) {
      setStN("err");
      setMsgN("⚠ Informe seu nome completo");
      ok = false;
    } else {
      setStN("ok");
      setMsgN("✓ Perfeito!");
    }
    if (!okEmail(email.trim())) {
      setStE("err");
      setMsgE("⚠ E-mail inválido");
      ok = false;
    } else {
      setStE("ok");
      setMsgE("✓ E-mail válido!");
    }
    if (!okPhone(tel)) {
      setStT("err");
      setMsgT("⚠ Número inválido");
      ok = false;
    } else {
      setStT("ok");
      setMsgT("✓ WhatsApp válido!");
    }
    return ok;
  };

  const enviarLead = async () => {
    setErrorTop("");
    if (!validateAll()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-imersao-lead", {
        body: { nome: nome.trim(), email: email.trim(), telefone: tel.trim() },
      });
      if (error) throw error;
      if (data && (data as any).ok === false) throw new Error((data as any).error || "Falha no envio");
      setSuccess(true);
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
    } catch (e: any) {
      setErrorTop(e?.message || "Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (s: FieldState) => (s === "err" ? "is-err" : s === "ok" ? "is-ok" : "");
  const msgClass = (s: FieldState) => "field-msg" + (s === "err" ? " err" : s === "ok" ? " ok" : "");

  return (
    <>
      <Helmet>
        <title>Seja Líder do Seu Destino · Bianchim Business</title>
        <meta
          name="description"
          content="Imersão Seja Líder do Seu Destino — Inteligência emocional aplicada à liderança real. 23 de Julho, São Carlos SP. Bianchim Business."
        />
        <meta name="theme-color" content="#05080F" />
        <meta property="og:title" content="Seja Líder do Seu Destino · Imersão Presencial 23/07" />
        <meta
          property="og:description"
          content="Inteligência emocional aplicada à liderança real. 23 de Julho · Cathedral Coworking · São Carlos SP"
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://bianchimbusiness.com.br/imersao-seja-lider-og.jpg" />
        <meta property="og:image:secure_url" content="https://bianchimbusiness.com.br/imersao-seja-lider-og.jpg" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:alt" content="Seja Líder do Seu Destino — Imersão presencial 23/07" />
        <meta property="og:url" content="https://bianchimbusiness.com.br/imersao-lider" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Seja Líder do Seu Destino · Imersão Presencial 23/07" />
        <meta name="twitter:description" content="Inteligência emocional aplicada à liderança real. 23/07 · São Carlos SP" />
        <meta name="twitter:image" content="https://bianchimbusiness.com.br/imersao-seja-lider-og.jpg" />
        <link rel="canonical" href="https://bianchimbusiness.com.br/imersao-lider" />
      </Helmet>

      {/* WHATSAPP FLUTUANTE */}
      <a href={WPP_INTERESSE} target="_blank" rel="noreferrer" className="wpp-float" aria-label="Falar no WhatsApp">
        <span className="wpp-float-label"><MessageCircle /> Falar com Junior</span>
        <MessageCircle />
      </a>

      {/* HERO */}
      <div className="hero">
        <div className="h-grid"></div>
        <div className="h-orb1"></div>
        <div className="h-orb2"></div>
        <div className="h-orb3"></div>
        <div className="h-nav">
          <Link to="/" className="h-brand-link" aria-label="Voltar para a página inicial">
            <img src={icone} alt="Bianchim Business" className="h-brand-logo" />
            <span className="h-brand-name">Bianchim Business</span>
          </Link>
          <div className="h-pill">
            <div className="h-dot"></div>
            <div className="h-pill-txt">IMERSÃO</div>
          </div>
        </div>
        <div className="h-body">
          <div className="h-eyebrow"><Zap /> Imersão Presencial · 23 de Julho</div>
          <h1 className="h-title">
            <span className="wh">SEJA</span>
            <span className="wh">LÍDER</span>
            <span className="cy">DO SEU</span>
            <span className="wh">DESTINO</span>
          </h1>
          <div className="h-divline"></div>
          <div className="h-quote">
            <p>
              Inteligência emocional aplicada à liderança real —<br />
              porque líderes não nascem, eles se constroem.
            </p>
          </div>
          <div className="dc">
            <div className="dc-top"><Diamond size={11} /> Uma noite de imersão presencial</div>
            <div className="dc-grid">
              <div className="dc-col"><div className="v">23/07</div><div className="l">Data</div></div>
              <div className="dc-sep"></div>
              <div className="dc-col"><div className="v">19H</div><div className="l">Início</div></div>
              <div className="dc-sep"></div>
              <div className="dc-col"><div className="v">22:30</div><div className="l">Término</div></div>
              <div className="dc-sep"></div>
              <div className="dc-col"><div className="v">3H30</div><div className="l">Imersão</div></div>
            </div>
            <div className="dc-addr">
              <div className="a1"><MapPin /> Coworking Digitaly Hub · Rua Dona Alexandrina, 876</div>
              <div className="a2">Vila Monteiro · São Carlos, SP</div>
            </div>
          </div>
        </div>
      </div>
      <div className="div-c"></div>

      {/* PARA QUEM */}
      <div className="sec s1">
        <div className="eyebrow">Para quem é essa imersão</div>
        <div className="sec-h">
          VOCÊ ESTÁ NO<br />LUGAR CERTO<br /><em>SE…</em>
        </div>
        <div className="sec-p">
          Criada para quem está pronto para parar de <strong>sobreviver na liderança</strong> e começar a{" "}
          <strong>liderar com presença, método e resultado real.</strong>
        </div>
        {([
          [Brain, "Suas emoções sabotam suas decisões de líder", "Transforme inteligência emocional na sua maior vantagem competitiva como gestor"],
          [Target, "Lidera uma equipe e quer resultados extraordinários", "Chega de gestão no improviso — lidere com método, autoridade e presença genuína"],
          [Flame, "Quer ser o líder que inspira e é seguido", "Liderança de verdade move pessoas. Construa autoridade que gera comprometimento real"],
          [Zap, "Quer transformação profunda em uma única noite", "Conteúdo denso, intenso e aplicável a partir do dia seguinte à imersão"],
        ] as const).map(([Ico, t, s], i) => (
          <div key={i} className="bc visible">
            <div className="bc-ico"><Ico /></div>
            <div className="bc-t">
              <strong>{t}</strong>
              <span>{s}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="div-c"></div>

      {/* CONTEÚDO */}
      <div className="sec s2">
        <div className="eyebrow">Conteúdo da imersão</div>
        <div className="sec-h">
          O QUE VOCÊ VAI<br /><em>TRANSFORMAR</em><br />NESSA NOITE
        </div>
        <div className="sec-p">
          7 pilares de liderança emocional que a maioria dos gestores <strong>nunca teve acesso</strong> — condensados em 3h30 de imersão de alto impacto.
        </div>
        {[
          ["Inteligência emocional: o que separa gestores de líderes de verdade", "Base científica aplicada à realidade do seu time e negócio"],
          ["Como liderar sob pressão sem perder o controle e a clareza", "Técnicas práticas para decisões em situações críticas"],
          ["O mapa do líder: autoconhecimento que gera autoridade real", "Descubra seus pontos fortes e pontos cegos como líder"],
          ["Como inspirar, engajar e mover pessoas com propósito", "Liderança que gera comprometimento — não obediência"],
          ["Comunicação de liderança: a linguagem que influencia e transforma", "Palavras que constroem pontes e engajam times"],
          ["Como dar feedbacks que desenvolvem — sem conflito, sem medo", "Metodologia prática de devolutivas que geram evolução"],
          ["Seu plano de liderança: estratégias para aplicar no dia seguinte", "Saia com um roteiro concreto e personalizado de liderança"],
        ].map(([t, s], i) => (
          <div key={i} className="ni visible">
            <div className="ni-n">{i + 1}</div>
            <div className="ni-t">
              {t}
              <small>{s}</small>
            </div>
          </div>
        ))}
      </div>
      <div className="div-c"></div>

      {/* DIFERENCIAIS */}
      <div className="sec s3">
        <div className="eyebrow">Experiência completa</div>
        <div className="sec-h">
          UMA NOITE QUE<br />MUDA A FORMA<br /><em>COMO VOCÊ LIDERA</em>
        </div>
        <div className="sec-p">
          Não é palestra. Não é curso online. É uma <strong>imersão presencial transformadora</strong> — criada para gerar resultados que você sente no dia seguinte.
        </div>
        <div className="dg">
          {([
            [ClipboardList, "Material Didático", "Conteúdo exclusivo para fixar e aplicar de imediato"],
            [Award, "Certificado", "Reconhecimento oficial da sua evolução como líder"],
            [Ticket, "Crachá Exclusivo", "Você faz parte de algo especial e transformador"],
            [Coffee, "Coffee Break", "Ambiente para conexão, troca e networking"],
          ] as const).map(([Ico, t, s], i) => (
            <div key={i} className="dcard visible">
              <span className="di"><Ico /></span>
              <strong>{t}</strong>
              <span>{s}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="div-c"></div>

      {/* MENTOR */}
      <div className="sec s4">
        <div className="eyebrow">Seu mentor nessa jornada</div>
        <div className="sec-h" style={{ marginBottom: 4 }}>
          JUNIOR<br /><em>BIANCHIM</em>
        </div>
        <div
          style={{
            fontSize: "8.5px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            color: "var(--c)",
            textTransform: "uppercase",
            textAlign: "center",
            marginBottom: 26,
          }}
        >
          Especialista em Vendas &amp; Liderança · Professor SENAC · CEO
        </div>
        <div className="s4-left">
          <div className="mentor-wrap">
            <img className="mentor-img" src="/imersao-mentor.jpg" alt="Junior Bianchim" />
            <div className="mentor-fade"></div>
          </div>
        </div>
        <div className="s4-right">
          <div className="stats">
            <div className="sbox"><div className="sv">20+</div><div className="sl">Anos liderando equipes</div></div>
            <div className="sbox"><div className="sv">MULTI</div><div className="sl">Multinacionais</div></div>
            <div className="sbox"><div className="sv">CEO</div><div className="sl">Bianchim Business</div></div>
          </div>
          {[
            <>Mais de <strong>20 anos de experiência prática em vendas e liderança comercial</strong>, formando e liderando equipes de alto desempenho em grandes empresas como a <strong>TIM Brasil</strong></>,
            <>Professor de <strong>Vendas, Marketing e Administração no SENAC</strong> — formando líderes e profissionais que geram resultados reais no mercado</>,
            <>CEO da <strong>Bianchim Business</strong>, onde transforma equipes comerciais em times de liderança e alta performance sustentável</>,
            <>Todo o conhecimento foi <strong>testado em campo</strong> — nada de teoria vazia, tudo de prática comprovada com foco em liderança e resultado</>,
          ].map((node, i) => (
            <div key={i} className="bio-row">
              <div className="bio-bar"></div>
              <div className="bio-txt">{node}</div>
            </div>
          ))}
          <div className="ach">
            {([
              [GraduationCap, "Professor SENAC", "Vendas, Mkt & Adm"],
              [Trophy, "Alta Performance", "Equipes & Liderança"],
              [TrendingUp, "Resultados Reais", "Testados em campo"],
              [Lightbulb, "Método Próprio", "Aplicável do dia 1"],
            ] as const).map(([Ico, t, s], i) => (
              <div key={i} className="ai">
                <div className="ai-ico"><Ico /></div>
                <div className="ai-txt">
                  {t}
                  <small>{s}</small>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
      <div className="div-y"></div>

      {/* OFERTA */}
      <div className="sec s5">
        <div className="eyebrow yl">Reserve sua experiência</div>
        <div className="sec-h">
          RESERVE SUA<br /><span className="yl">EXPERIÊNCIA</span>
        </div>
        <div className="sec-p" style={{ marginBottom: 20 }}>
          Turma reduzida para garantir a <strong>máxima profundidade</strong> e experiência individual de cada participante.
        </div>

        <div className="cl cl-top">
          {[
            "Imersão presencial de 3h30 de alto impacto",
            "Material didático exclusivo da imersão",
            "Certificado de conclusão",
            "Crachá personalizado do evento",
            "Coffee break incluso",
            "Networking com outros líderes",
            "Plano de ação de liderança personalizado",
          ].map((t, i) => (
            <div key={i} className="ci">
              <div className="ck"><Check /></div>
              <div className="ct">{t}</div>
            </div>
          ))}
        </div>

        <div className="s5-left">
          <div className="price-box">
            <div className="price-head"><Diamond size={11} /> Experiência Completa · Seu Investimento</div>
            <div className="price-body">
              <div className="price-lbl">Investimento</div>
              <div className="price-row">
                <span className="curr">R$</span>
                <span className="int">259</span>
              </div>
              <div className="price-inst">até 4x sem juros no cartão</div>
              <div className="price-or">ou</div>
              <div className="price-pix-lbl">À vista via PIX com 10% de desconto</div>
              <div className="price-pix-val">R$ 233,99</div>
              <div className="price-badge">10% OFF NO PIX</div>
              <div className="price-note">Mais de 4 parcelas: disponível com juros · Consulte condições</div>
            </div>
          </div>

          <div className="proof">
            <div className="proof-stars">
              <Star /><Star /><Star /><Star /><Star />
            </div>
            <div className="proof-txt">
              <em>100%</em> dos participantes avaliaram a experiência com <em>nota máxima.</em>
            </div>
            <div className="proof-sub">Validação real de quem viveu a imersão</div>
          </div>

          <div className="local">
            <div className="local-ico"><MapPin /></div>
            <div>
              <div className="local-lbl">Local do Evento</div>
              <div className="local-name">Coworking Digitaly Hub</div>
              <div className="local-addr">
                Rua Dona Alexandrina, 876<br />Vila Monteiro · São Carlos, SP
              </div>
              <div className="local-when">23 de Julho · Das 19h às 22h30</div>
            </div>
          </div>

          <div className="urg">
            <span className="urg-ico"><Zap /></span>
            <div className="urg-txt">
              Vagas limitadas para preservar a profundidade da experiência
              <span>Turma reduzida · Máxima atenção a cada participante</span>
            </div>
          </div>
        </div>

        <div className="s5-right">
          {/* FORMULÁRIO */}
          <div className="form-wrap">
            <div className="form-head">
              <div className="form-head-title"><Diamond size={11} /> Garanta sua vaga agora</div>
              <div className="form-head-sub">Preencha seus dados para confirmar sua presença</div>
            </div>
            <div className="form-body">
              {!success ? (
                <div>
                  <div className="form-intro">
                    Informe seus dados para <strong>confirmar sua presença</strong> e receber todas as informações no seu e-mail e WhatsApp.
                  </div>
                  <div className="field">
                    <label>Nome Completo</label>
                    <div className="field-inner">
                      <span className="field-icon"><User /></span>
                      <input
                        type="text"
                        placeholder="Seu nome completo"
                        autoComplete="name"
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        onBlur={() => {
                          if (nome.trim().length < 3) {
                            setStN("err");
                            setMsgN("⚠ Informe seu nome completo");
                          } else {
                            setStN("ok");
                            setMsgN("✓ Perfeito!");
                          }
                        }}
                        className={inputClass(stN)}
                      />
                    </div>
                    <div className={msgClass(stN)}>{msgN}</div>
                  </div>
                  <div className="field">
                    <label>E-mail</label>
                    <div className="field-inner">
                      <span className="field-icon"><Mail /></span>
                      <input
                        type="email"
                        placeholder="seu@email.com"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => {
                          if (okEmail(email.trim())) {
                            setStE("ok");
                            setMsgE("✓ E-mail válido!");
                          } else {
                            setStE("err");
                            setMsgE("⚠ E-mail inválido");
                          }
                        }}
                        className={inputClass(stE)}
                      />
                    </div>
                    <div className={msgClass(stE)}>{msgE}</div>
                  </div>
                  <div className="field">
                    <label>WhatsApp</label>
                    <div className="field-inner">
                      <span className="field-icon"><Phone /></span>
                      <input
                        type="tel"
                        placeholder="(16) 99999-9999"
                        autoComplete="tel"
                        maxLength={15}
                        value={tel}
                        onChange={(e) => setTel(maskPhone(e.target.value))}
                        onBlur={() => {
                          if (okPhone(tel)) {
                            setStT("ok");
                            setMsgT("✓ WhatsApp válido!");
                          } else {
                            setStT("err");
                            setMsgT("⚠ Número inválido");
                          }
                        }}
                        className={inputClass(stT)}
                      />
                    </div>
                    <div className={msgClass(stT)}>{msgT}</div>
                  </div>
                  {errorTop && (
                    <div className="field-msg err" style={{ textAlign: "center", marginBottom: 8 }}>
                      ⚠ {errorTop}
                    </div>
                  )}
                  <button type="button" className="btn-confirm" onClick={enviarLead} disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner"></span> Enviando...
                      </>
                    ) : (
                      "CONFIRMAR E GARANTIR MINHA VAGA"
                    )}
                  </button>
                  <div className="form-note"><Lock size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} /> Dados protegidos · Não compartilhamos com terceiros</div>
                </div>
              ) : (
                <div className="form-success" style={{ display: "block" }}>
                  <span className="form-success-icon"><CheckCircle2 /></span>
                  <div className="form-success-title">VAGA CONFIRMADA!</div>
                  <div className="form-success-sub">
                    Enviamos um e-mail para <strong>{email}</strong> com os links de pagamento.
                    <br />
                    <strong>Escolha sua forma de pagamento abaixo.</strong>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={"unlock-hint" + (success ? " unlocked" : "")}>
            {success ? <><Unlock /> Pagamento liberado · Escolha sua forma</> : <><Lock /> Preencha seus dados acima para liberar o pagamento</>}
          </div>
          <a
            href={success ? LINK_4X : undefined}
            target="_blank"
            rel="noreferrer"
            className={"btn " + (success ? "btn-c" : "btn-locked")}
          >
            {!success && <Lock size={15} />} PARCELAR EM ATÉ 4X SEM JUROS
          </a>
          <a
            href={success ? LINK_PIX : undefined}
            target="_blank"
            rel="noreferrer"
            className={"btn " + (success ? "btn-y" : "btn-locked")}
          >
            {!success && <Lock size={15} />} PAGAR NO PIX · R$ 233,99
          </a>
          <a href={WPP_INFO} target="_blank" rel="noreferrer" className="btn btn-w">
            <MessageCircle size={17} />
            FALAR COM JUNIOR BIANCHIM
          </a>
          <div className="secure">
            <div className="secure-ico"><ShieldCheck /></div>
            <div className="secure-txt">
              <strong>Pagamento 100% seguro</strong>Processado pelo Mercado Pago com proteção total
            </div>
          </div>
        </div>
      </div>
      <div className="div-y"></div>

      {/* DEPOIMENTOS */}
      <div className="sec depo">
        <div className="eyebrow">Depoimentos reais</div>
        <div className="sec-h">
          O QUE DIZEM<br />QUEM JÁ<br /><em>VIVEU A IMERSÃO</em>
        </div>
        <div className="sec-p">
          Empresários e profissionais que <strong>transformaram sua liderança</strong> e seu negócio.
        </div>
        <div className="depo-grid">
          {[
            { src: "/depoimentos/andrea-drittlhuber.mp4", nome: "Andrea Drittlhuber", cargo: "Empresária · Arte e Fotografia" },
            { src: "/depoimentos/aliadine-blanco.mp4", nome: "Aliádine Daniele da Silva Blanco", cargo: "Empresária · Donna Thê Confeitaria e Café" },
            { src: "/depoimentos/paulo-bispo.mp4", nome: "Paulo Bispo", cargo: "Mentor de Aprimoramento Pessoal " },
            { src: "/depoimentos/gilmara.mp4", nome: "Gilmara", cargo: "Empresária · Sabonetes Artesanais" },
          ].map((d, i) => (
            <div key={i} className="depo-card">
              <video
                src={d.src}
                controls
                preload="metadata"
                playsInline
                className="depo-video"
              />
              <div className="depo-info">
                <div className="depo-nome">{d.nome}</div>
                <div className="depo-cargo">{d.cargo}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="div-y"></div>

      {/* FINAL */}
      <div className="final">
        <div className="f-orb1"></div>
        <div className="f-orb2"></div>
        <div className="f-ey"><Diamond size={10} style={{ verticalAlign: "-1px", marginRight: 6 }} /> Vagas limitadas para preservar a profundidade da experiência</div>
        <div className="f-h">
          SUA<br />LIDERANÇA<br /><em>COMEÇA AQUI.</em>
        </div>
        <div className="f-p">
          Em <strong>3h30</strong>, você vai sair diferente de como entrou.<br />
          Mais <span className="yl">consciente.</span>
          <br />
          Mais <span className="yl">preparado.</span>
          <br />
          Mais <span className="yl">líder.</span>
        </div>
        <a
          href={LINK_4X}
          target="_blank"
          rel="noreferrer"
          className="btn btn-c"
          style={{ fontSize: 13, padding: 20, marginBottom: 10 }}
        >
          QUERO VIVER ESSA IMERSÃO
        </a>
        <a href={LINK_PIX} target="_blank" rel="noreferrer" className="btn btn-y">
          PIX À VISTA · R$ 233,99
        </a>
        <div className="f-brand">
          Bianchim Business · <span>Liderança &amp; Desenvolvimento Comercial</span>
        </div>
      </div>
    </>
  );
}
